import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitRegistration, verifyPayment, getRegistrationStatus } from '../../services/registrationService';
import { getErrorMessage } from '../../api/client';
import './Registration.css';

const initialFormData = {
  name: '',
  age: '',
  home_town: '',
  present_location: '',
  email: '',
  phone: '',
  height_cm: '',
  weight_kg: '',
  chest_inches: '',
  waist_inches: '',
  hip_inches: '',
  shoe_size: '',
  skin_tone: '',
  eye_colour: '',
  hair_colour: '',
  father_occupation: '',
  marital_status: ''
};

const Registration = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [photo, setPhoto] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    setPhoto(e.target.files?.[0] || null);
  };

  const pollForRegistrationNumber = async (orderId, attempts = 5) => {
    for (let i = 0; i < attempts; i += 1) {
      try {
        const statusResult = await getRegistrationStatus(orderId);
        if (statusResult?.data?.registration_number) {
          return statusResult.data.registration_number;
        }
      } catch (pollError) {
        // ignore and retry
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    return null;
  };

  const openRazorpayCheckout = (orderData) => {
    if (!window.Razorpay) {
      setSubmitError('Payment gateway failed to load. Please refresh the page and try again.');
      setIsSubmitting(false);
      return;
    }

    const options = {
      key: orderData.key_id,
      amount: orderData.amount,
      currency: orderData.currency || 'INR',
      name: 'SRK Production',
      description: 'Audition Registration Fee',
      order_id: orderData.order_id,
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone
      },
      theme: { color: '#c9a961' },
      handler: async (response) => {
        try {
          const verifyResult = await verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });

          let registrationNumber = verifyResult?.registration_number || null;
          if (!registrationNumber) {
            registrationNumber = await pollForRegistrationNumber(orderData.order_id);
          }

          navigate('/payment-success', {
            state: {
              registrationNumber,
              orderId: orderData.order_id
            }
          });
        } catch (verifyError) {
          setSubmitError(getErrorMessage(verifyError, 'Payment was captured but verification failed. Please contact support with your order details.'));
          setIsSubmitting(false);
        }
      },
      modal: {
        ondismiss: () => {
          setStatusMessage('');
          setSubmitError('Payment was not completed. You can try again.');
          setIsSubmitting(false);
        }
      }
    };

    const razorpayCheckout = new window.Razorpay(options);
    razorpayCheckout.on('payment.failed', (response) => {
      setSubmitError(response?.error?.description || 'Payment failed. Please try again.');
      setIsSubmitting(false);
    });
    razorpayCheckout.open();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatusMessage('');
    setSubmitError('');

    try {
      const payload = { ...formData };
      if (photo) {
        payload.photo = photo;
      }

      const result = await submitRegistration(payload);
      setStatusMessage('Registration details saved. Redirecting to payment...');
      openRazorpayCheckout(result.data);
    } catch (error) {
      setSubmitError(getErrorMessage(error, 'Unable to submit registration.'));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-page">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="registration-form-wrapper">
              <h3 className="text-white text-center mb-5">
                Fill out the form below to showcase your talent with us
              </h3>

              <div className="mb-4">
              <form onSubmit={handleSubmit} className="d-flex flex-column align-items-center">
                <div className="registration-fields">
                {/* Name / Age */}
                <div className="row w-100 gx-3">
                  <div className="col-md-6 mb-4">
                    <label htmlFor="name" className="form-label text-white">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg w-100"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      minLength="2"
                      maxLength="150"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-4">
                    <label htmlFor="age" className="form-label text-white">
                      Age <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-lg w-100"
                      id="age"
                      name="age"
                      value={formData.age}
                      onChange={handleChange}
                      min="5"
                      max="99"
                      required
                    />
                  </div>
                </div>

                {/* Email / Phone */}
                <div className="row w-100 gx-3">
                  <div className="col-md-6 mb-4">
                    <label htmlFor="email" className="form-label text-white">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control form-control-lg w-100"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-4">
                    <label htmlFor="phone" className="form-label text-white">
                      Phone <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className="form-control form-control-lg w-100"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Hometown / Present Location */}
                <div className="row w-100 gx-3">
                  <div className="col-md-6 mb-4">
                    <label htmlFor="home_town" className="form-label text-white">
                      Home Town <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg w-100"
                      id="home_town"
                      name="home_town"
                      value={formData.home_town}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-4">
                    <label htmlFor="present_location" className="form-label text-white">
                      Present Location <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg w-100"
                      id="present_location"
                      name="present_location"
                      value={formData.present_location}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Physical Measurements Row 1 */}
                <div className="row w-100 gx-3">
                  <div className="col-md-6 mb-4">
                    <label htmlFor="height_cm" className="form-label text-white">
                      Height (cm) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="50"
                      max="300"
                      className="form-control form-control-lg w-100"
                      id="height_cm"
                      name="height_cm"
                      value={formData.height_cm}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-4">
                    <label htmlFor="weight_kg" className="form-label text-white">
                      Weight (kg) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="10"
                      max="300"
                      className="form-control form-control-lg w-100"
                      id="weight_kg"
                      name="weight_kg"
                      value={formData.weight_kg}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Physical Measurements Row 2 */}
                <div className="row w-100 gx-3">
                  <div className="col-md-4 mb-4">
                    <label htmlFor="chest_inches" className="form-label text-white">
                      Chest (inches) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="10"
                      max="100"
                      className="form-control form-control-lg w-100"
                      id="chest_inches"
                      name="chest_inches"
                      value={formData.chest_inches}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-4 mb-4">
                    <label htmlFor="waist_inches" className="form-label text-white">
                      Waist (inches) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="10"
                      max="100"
                      className="form-control form-control-lg w-100"
                      id="waist_inches"
                      name="waist_inches"
                      value={formData.waist_inches}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-4 mb-4">
                    <label htmlFor="hip_inches" className="form-label text-white">
                      Hip (inches) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="10"
                      max="100"
                      className="form-control form-control-lg w-100"
                      id="hip_inches"
                      name="hip_inches"
                      value={formData.hip_inches}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Skin Tone / Eye Colour */}
                <div className="row w-100 gx-3">
                  <div className="col-md-6 mb-4">
                    <label htmlFor="skin_tone" className="form-label text-white">
                      Skin Tone <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-lg w-100"
                      id="skin_tone"
                      name="skin_tone"
                      value={formData.skin_tone}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select skin tone</option>
                      <option value="Very Fair">Very Fair</option>
                      <option value="Fair">Fair</option>
                      <option value="Wheatish">Wheatish</option>
                      <option value="Medium">Medium</option>
                      <option value="Dusky">Dusky</option>
                      <option value="Dark">Dark</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-4">
                    <label htmlFor="eye_colour" className="form-label text-white">
                      Eye Colour <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg w-100"
                      id="eye_colour"
                      name="eye_colour"
                      value={formData.eye_colour}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Hair Colour / Shoe Size */}
                <div className="row w-100 gx-3">
                  <div className="col-md-6 mb-4">
                    <label htmlFor="hair_colour" className="form-label text-white">
                      Hair Colour <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg w-100"
                      id="hair_colour"
                      name="hair_colour"
                      value={formData.hair_colour}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-4">
                    <label htmlFor="shoe_size" className="form-label text-white">
                      Shoe Size <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      className="form-control form-control-lg w-100"
                      id="shoe_size"
                      name="shoe_size"
                      value={formData.shoe_size}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Father Occupation / Marital Status */}
                <div className="row w-100 gx-3">
                  <div className="col-md-6 mb-4">
                    <label htmlFor="father_occupation" className="form-label text-white">
                      Father / Mother Occupation <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg w-100"
                      id="father_occupation"
                      name="father_occupation"
                      value={formData.father_occupation}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-4">
                    <label htmlFor="marital_status" className="form-label text-white">
                      Marital Status <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-lg w-100"
                      id="marital_status"
                      name="marital_status"
                      value={formData.marital_status}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select marital status</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                    </select>
                  </div>
                </div>

                {/* Photo */}
                <div className="mb-4 w-100">
                  <label htmlFor="photo" className="form-label text-white">
                    Photo (optional, JPEG/PNG/WebP, max 5MB)
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="form-control form-control-lg w-100"
                    id="photo"
                    name="photo"
                    onChange={handlePhotoChange}
                  />
                </div>

                {statusMessage && <div className="submission-message success w-100">{statusMessage}</div>}
                {submitError && <div className="submission-message error w-100">{submitError}</div>}

                <div className="w-100 pt-3">
                  <button
                    type="submit"
                    className="btn btn-submit fw-bold w-100"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : 'Submit & Pay Registration Fee'}
                  </button>
                </div>
                </div>
              </form>
              </div>            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;
